import React, { useState } from "react";
import Select from "react-select";
import { useCourses } from "../../hooks/useCourses";
import CustomPagination from "../../common/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import { TableStatus } from "../../components/TableStatus";
import CourseModal from "./CourseModal";
import { DURATION_OPTIONS, LEVEL_OPTIONS, PRICING_OPTIONS, SORT_OPTIONS } from "./courseOptions";

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "38px",
        borderColor: "#dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#dee2e6" },
    }),
    menu: (base) => ({ ...base, zIndex: 1050 }),
};

export default function CourseTablePage() {
    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);

    const [appliedFilters, setAppliedFilters] = useState({
        search: "",
        durationUnit: "",
        level: "",
        pricingTier: "",
        sort: "",
        minPrice: "",
        maxPrice: "",
    });

    const [tempFilters, setTempFilters] = useState({
        search: "",
        durationUnit: "",
        level: "",
        pricingTier: "",
        sort: "",
        minPrice: "",
        maxPrice: "",
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null);
    const [courseToDelete, setCourseToDelete] = useState(null);

    const {
        courses,
        pagination,
        isLoading,
        isError,
        error,
        createCourse,
        updateCourse,
        deleteCourse,
        isDeleting,
        isCreating,
        isUpdating,
    } = useCourses(
        currentPage,
        itemsPerPage,
        appliedFilters.search,
        appliedFilters.durationUnit,
        appliedFilters.level,
        appliedFilters.pricingTier,
        appliedFilters.sort,
        appliedFilters.minPrice,
        appliedFilters.maxPrice
    );

    const handleInputChange = (field, value) => {
        setTempFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleFilterSelectChange = (field, selectedOption) => {
        setTempFilters((prev) => ({
            ...prev,
            [field]: selectedOption ? selectedOption.value : "",
        }));
    };

    const handleSearch = () => {
        setAppliedFilters(tempFilters);
        setCurrentPage(1);
    };

    const handleReset = () => {
        const resetState = { search: "", durationUnit: "", level: "", pricingTier: "", sort: "", minPrice: "", maxPrice: "" };
        setTempFilters(resetState);
        setAppliedFilters(resetState);
        setCurrentPage(1);
    };

    const handleOpenModal = (course = null) => {
        setEditingCourse(course);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (editingCourse) {
                await updateCourse({ id: editingCourse._id, data: formData });
            } else {
                await createCourse(formData);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to save course", error);
        }
    };

    const handleDelete = async () => {
        if (!courseToDelete) return;
        await deleteCourse(courseToDelete._id);
        setCourseToDelete(null);
        if (courses.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    return (
        <>
            <div className="card shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                    <h4 className="mb-0 text-primary-emphasis">Course Management</h4>
                    <button className="btn btn-success btn-sm" onClick={() => handleOpenModal()}>
                        <i className="fas fa-plus mr-1"></i> Add Course
                    </button>
                </div>

                <div className="card-body border-bottom">
                    <div className="row g-2 mb-2">
                        <div className="col-md-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search Title..."
                                value={tempFilters.search}
                                onChange={(e) => handleInputChange("search", e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </div>

                        <div className="col-md-3">
                            <Select
                                options={SORT_OPTIONS}
                                value={SORT_OPTIONS.find((opt) => opt.value === tempFilters.sort) || null}
                                onChange={(opt) => handleFilterSelectChange("sort", opt)}
                                placeholder="Sort By..."
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="col-md-3">
                            <Select
                                options={DURATION_OPTIONS}
                                value={DURATION_OPTIONS.find((opt) => opt.value === tempFilters.durationUnit) || null}
                                onChange={(opt) => handleFilterSelectChange("durationUnit", opt)}
                                placeholder="Duration..."
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="col-md-3">
                            <Select
                                options={LEVEL_OPTIONS}
                                value={LEVEL_OPTIONS.find((opt) => opt.value === tempFilters.level) || null}
                                onChange={(opt) => handleFilterSelectChange("level", opt)}
                                placeholder="Level..."
                                isClearable
                                styles={selectStyles}
                            />
                        </div>
                    </div>

                    <div className="row g-2">
                        <div className="col-md-3">
                            <Select
                                options={PRICING_OPTIONS}
                                value={PRICING_OPTIONS.find((opt) => opt.value === tempFilters.pricingTier) || null}
                                onChange={(opt) => handleFilterSelectChange("pricingTier", opt)}
                                placeholder="Pricing..."
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Min Price..."
                                value={tempFilters.minPrice}
                                onChange={(e) => handleInputChange("minPrice", e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </div>

                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Max Price..."
                                value={tempFilters.maxPrice}
                                onChange={(e) => handleInputChange("maxPrice", e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </div>

                        <div className="col-md-3 d-flex gap-2">
                            <button className="btn btn-primary w-50 mr-2" onClick={handleSearch}>
                                Search
                            </button>
                            <button className="btn btn-outline-dark w-50" onClick={handleReset}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>No</th>
                                    <th>Image</th>
                                    <th>Title</th>
                                    <th>Institution</th>
                                    <th>Level</th>
                                    <th>Duration</th>
                                    <th>Sector</th>
                                    <th>Certificate</th>
                                    <th>Pricing</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus
                                    status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                    error={error}
                                    dataLength={courses.length}
                                    colSpan={10}
                                />

                                {!isLoading &&
                                    courses.map((course, index) => (
                                        <tr key={course._id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                            <td>
                                                {course?.imageUrl ? (
                                                    <img
                                                        src={course.imageUrl}
                                                        alt="Course"
                                                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                                                    />
                                                ) : (
                                                    <div style={{ width: "40px", height: "40px", backgroundColor: "#e9ecef", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <i className="fas fa-image text-secondary"></i>
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <div className="fw-bold d-flex align-items-center">
                                                    {course?.title}
                                                    {course?.isPopular && (
                                                        <span className="badge bg-danger ms-2" style={{ fontSize: "0.7rem" }}>
                                                            <i className="fas fa-fire me-1"></i>Popular
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td>{course?.institution}</td>
                                            <td>{course?.level}</td>
                                            <td>
                                                {course?.duration} {course?.durationUnit}
                                            </td>
                                            <td>{course?.sector}</td>
                                            <td>
                                                {course?.certificate ? (
                                                    <span className="badge bg-success">Yes</span>
                                                ) : (
                                                    <span className="badge bg-secondary">No</span>
                                                )}
                                            </td>
                                            <td>{course.pricingTier}</td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    {course?.link && (
                                                        <a
                                                            href={course.link}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="btn btn-sm btn-outline-info mr-2"
                                                            title="Visit Course"
                                                        >
                                                            Visit
                                                        </a>
                                                    )}

                                                    <button
                                                        className="btn btn-sm btn-outline-primary mr-2"
                                                        onClick={() => handleOpenModal(course)}
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => setCourseToDelete(course)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {pagination?.totalPages > 1 && (
                    <div className="card-footer bg-white">
                        <CustomPagination
                            currentPage={Number(currentPage)}
                            totalPages={Number(pagination.totalPages)}
                            totalItems={Number(pagination.totalRecords)}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            <CourseModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                initialData={editingCourse}
                isLoading={isCreating || isUpdating}
            />

            <ConfirmationModal
                show={courseToDelete !== null}
                onClose={() => setCourseToDelete(null)}
                onConfirm={handleDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                confirmButtonVariant="danger"
                isLoading={isDeleting}
            >
                Are you sure you want to delete <strong>{courseToDelete?.title}</strong>?
            </ConfirmationModal>
        </>
    );
}